import React, { useEffect, useState } from 'react';

import AvatarGroup, { type AvatarProps } from '@atlaskit/avatar-group';
import type { CollabEditProvider, Provider } from '@atlaskit/collab-provider';
import type { CollabEditOptions, ProviderParticipant } from '@atlaskit/editor-common/collab';
import { Box, xcss } from '@atlaskit/primitives';

import { Attribution, ErrorBoundary } from '@confluence/error-boundary';
import { fg } from '@confluence/feature-gating';
import { useNativeCollabState } from '@confluence/native-collab';

import { EditorPresenceAvatarGroupLivePagePlaceholder } from './EditorPresenceAvatarGroupLivePagePlaceholder';
import { EditorPresenceAvatarGroupPlaceholder } from './EditorPresenceAvatarGroupPlaceholder';
import { InviteToEditButton } from './InviteToEditButton';
import { generateAvatarDescriptorFromParticipant } from './generateAvatarDescriptorFromParticipant';
import { useAIProviderAvatarGroup } from './useAIProviderAvatarGroup';
import { useUnifiedShareDialogEligible } from '@confluence/experiment-unified-share-dialog';

// The max includes the (+n) button
const aiProviderGroupWrapper = xcss({ paddingLeft: 'space.100', paddingRight: 'space.200' });
const MAX_AVATARS = 3;

type EditorPresenceAvatarGroupProps = {
	collabEditOption?: CollabEditOptions;
	isLivePage?: boolean;
};

const EditorPresenceAvatarGroupComponent = ({
	collabEditOption,
	isLivePage,
}: EditorPresenceAvatarGroupProps) => {
	const [{ collabEditProvider }] = useNativeCollabState();
	const [avatarDescriptors, setAvatarDescriptors] = useState<AvatarProps[] | null>(null);
	const [aiProviderParticipants, setAIProviderParticipants] = useState<ProviderParticipant[]>([]);
	const { aiProviderAvatarGroup } = useAIProviderAvatarGroup({ aiProviderParticipants });
	const { isUSDExperimentEnabled } = useUnifiedShareDialogEligible();

	useEffect(() => {
		const makeAvatarsFromParticipants = (
			collabEditProvider: (CollabEditProvider & Provider) | null,
		) => {
			const sessionId = collabEditProvider?.getMetadata()?.sessionId;
			const participants = collabEditProvider?.getParticipants();

			// put the current user first in the list of participants, then convert all participants to
			// Avatar descriptors (AvatarProps) which will be converted to actual Avatars in the AvatarGroup
			setAvatarDescriptors(
				participants
					? participants
							.sort((p) => (p.sessionId === sessionId ? -1 : 1))
							.map(generateAvatarDescriptorFromParticipant)
					: null,
			);

			const fetchedAIProviderParticipants = collabEditProvider?.getAIProviderParticipants();
			setAIProviderParticipants(fetchedAIProviderParticipants || []);
		};

		// Someone has joined or left participation in editing of the page
		const handleUpdatePresence = () => {
			makeAvatarsFromParticipants(collabEditProvider);
		};

		if (!collabEditProvider) return;

		makeAvatarsFromParticipants(collabEditProvider);

		collabEditProvider.on('presence', handleUpdatePresence);
		return () => {
			collabEditProvider.off('presence', handleUpdatePresence);
		};
	}, [collabEditProvider]);

	if (!Array.isArray(avatarDescriptors) || avatarDescriptors.length === 0 || !avatarDescriptors) {
		if (isLivePage) {
			return <EditorPresenceAvatarGroupLivePagePlaceholder />;
		} else {
			return <EditorPresenceAvatarGroupPlaceholder />;
		}
	}

	return (
		<>
			{aiProviderAvatarGroup && <Box xcss={aiProviderGroupWrapper}>{aiProviderAvatarGroup}</Box>}
			<AvatarGroup
				appearance="stack"
				maxCount={MAX_AVATARS}
				data={avatarDescriptors}
				testId="editor-in-context-collaborators"
			/>
			{!(fg('confluence_frontend_unified_restrict_and_share') || isUSDExperimentEnabled) && (
				<InviteToEditButton inviteToEditComponent={collabEditOption?.inviteToEditComponent} />
			)}
		</>
	);
};

export const EditorPresenceAvatarGroup = (props: EditorPresenceAvatarGroupProps) => {
	return (
		<ErrorBoundary attribution={Attribution.PAGE_MODES}>
			<EditorPresenceAvatarGroupComponent {...props} />
		</ErrorBoundary>
	);
};
